// src/services/ImageService.js
import { getToken } from '../utils/secureStorage';
// ===== آدرس‌های پایه =====
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';   // json-server

// 🆕 تشخیص پویا — کاملاً هم‌راستا با apiClient.js در هسته‌ی books
// دلیل: در dev و شبکه، سرور آپلود روی پورت ۳۰۰۲ است و باید مستقیم صدا زده شود.
// در production واقعی، nginx مسیر /upload و /media را به ۳۰۰۲ پروکسی می‌کند.
const isLocalHostname = (h) =>
  h === 'localhost' || h === '127.0.0.1' || h === '::1' ||
  /^192\.168\./.test(h) || /^10\./.test(h) || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(h);

const detectUploadBase = () => {
  // ۱. اولویت اول با env (برای APK و Production)
  const configured = process.env.REACT_APP_FILE_API_URL || process.env.REACT_APP_UPLOAD_URL;
  if (configured) {
    return configured.replace(/\/+$/, '');
  }
  // ۲. اولویت با hostname فعلی (dev محلی + شبکه‌ی خصوصی)
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (isLocalHostname(h)) return `http://${h}:3002`;
  }
  // ۳. production fallback (nginx proxy)
  return '/upload';
};

const UPLOAD_API_BASE = detectUploadBase();

// ============================================================
// ===== آپلود تصویر =====
// ============================================================
export const uploadImage = async (file, teacherId, examId = null, type = 'cover', questionIndex = null) => {
  if (!file) throw new Error('فایل برای آپلود الزامی است');
  if (!teacherId) throw new Error('شناسه معلم الزامی است');

  const formData = new FormData();
  formData.append('image', file);
  formData.append('teacherId', teacherId);
  formData.append('examId', examId || '');
  formData.append('type', type);
  if (questionIndex !== null && questionIndex !== undefined) {
    formData.append('questionIndex', String(questionIndex));
  }

  try {
    const token = getToken();
    const response = await fetch(`${UPLOAD_API_BASE}/api/upload`, {
      method: 'POST',
      headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: formData,
    });

    if (!response.ok) {
      let errorMessage = 'خطا در آپلود تصویر';
      try {
        const errorData = await response.json();
        if (errorData.error) errorMessage = errorData.error;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new Error(`${errorMessage} (کد ${response.status})`);
    }

    const data = await response.json();
    if (!data.path) throw new Error('مسیر تصویر در پاسخ سرور موجود نیست');
    return data.path;
  } catch (error) {
    console.error('❌ Image upload error:', error);
    throw error;
  }
};

// ============================================================
// ===== انتقال تصاویر از temp به پوشه نهایی =====
// ============================================================
export const moveExamImages = async (teacherId, examId, tempId) => {
  if (!teacherId || !examId || !tempId) {
    throw new Error('teacherId, examId و tempId هر سه الزامی هستند');
  }

  try {
    const token = getToken();
    const response = await fetch(`${UPLOAD_API_BASE}/api/move-exam-images`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ teacherId, examId, tempId }),
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'خطا در انتقال تصاویر');
    return data;
  } catch (error) {
    console.error('❌ Move images error:', error);
    throw error;
  }
};

// ============================================================
// ===== حذف یک فایل =====
// ============================================================
export const deleteImage = async (path) => {
  if (!path) return true;
  try {
    const token = getToken();
    const response = await fetch(`${UPLOAD_API_BASE}/api/delete`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ path }),
    });
    if (!response.ok) return false;
    let data = null;
    try { data = await response.json(); } catch {}
    // فایل حذف شد یا از قبل نبود → موفق
    if (data?.success === true) return true;
    if (data?.success === false && data?.notFound === true) return true; // already gone
    return false;
  } catch (error) {
    console.error('❌ Delete error:', error);
    return false;
  }
};

// ============================================================
// ===== حذف کل پوشه آزمون =====
// ============================================================
export const deleteExamImages = async (teacherId, examId) => {
  if (!teacherId || !examId) return true;
  try {
    const token = getToken();
    const response = await fetch(`${UPLOAD_API_BASE}/api/delete-exam`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ teacherId, examId }),
    });
    return response.ok;
  } catch (error) {
    console.error('❌ Delete exam images error:', error);
    return false;
  }
};

// ============================================================
// ===== ساخت مسیر کامل برای نمایش تصویر =====
// ============================================================
export const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!path.startsWith('/media')) {
    path = `/media${path.startsWith('/') ? '' : '/'}${path}`;
  }
  // برای نمایش از همان سرور آپلود (پورت 3002) استفاده می‌شود
  return `${UPLOAD_API_BASE}${path}`;
};

// ============================================================
// ===== خروجی پیش‌فرض =====
// ============================================================
export default {
  uploadImage,
  moveExamImages,
  deleteImage,
  deleteExamImages,
  getImageUrl,
};